import React, { useState, useCallback } from 'react';
import { Grid } from '@material-ui/core';

import { searchHinovaVeiculo } from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaVeiculo } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import VehicleDetails from '../VehicleDetails';

export default function VehicleSearch() {
  const [placa, setPlaca] = useState<string>('');
  const [vehicles, setVehicles] = useState<HinovaVeiculo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<HinovaVeiculo | null>(null);
  const [openVehicleDetails, setOpenVehicleDetails] = useState<boolean>(false);

  const searchVehicle = useCallback(async () => {
    if (!placa.trim()) {
      setSeverity('warning');
      setMessageSnack('Por favor, informe uma placa para pesquisar');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const response = await searchHinovaVeiculo(placa.trim());

      if (response.status === 200 && response.data) {
        const data = response.data;
        const vehiclesData = Array.isArray(data) ? data : [data];
        setVehicles(vehiclesData);
      } else {
        setVehicles([]);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível pesquisar veículos. Tente novamente.',
      );
      setOpenSnackbar(true);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [placa]);

  const handleViewDetails = (vehicle: HinovaVeiculo) => {
    setSelectedVehicle(vehicle);
    setOpenVehicleDetails(true);
  };

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Placa'
            name='placa'
            type='text'
            value={placa}
            onChange={e => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            error={null}
            placeholder='Ex: ABC1234'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            label='Pesquisar'
            loading={loading}
            onClick={searchVehicle}
            filled
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      </Grid>

      <Table
        isLoading={loading}
        data={vehicles}
        columns={{

          codigo_tipo_veiculo:{
            title: 'Código',
          },
          placa: {
            title: 'Placa',
          },
          modelo: {
            title: 'Modelo',
          },
          marca: {
            title: 'Marca',
          },
          chassi: {
            title: 'Chassi',
            render: rowData => rowData.chassi || '-',
          },
          renavam: {
            title: 'RENAVAM',
            render: rowData => rowData.renavam || '-',
          },
          tipo: {
            title: 'Tipo',
            render: rowData => rowData.tipo || '-',
          },
          valor_fipe: {
            title: 'Fipe',
            render: rowData => (rowData.valor_fipe ? `R$ ${rowData.valor_fipe.toFixed(2)}` : '-'),
          },
          cpf: {
            title: 'Cpf',
            render: rowData => rowData.cpf || '-',
          },
        }}
        actions={[
          {
            icon: () => <span>👁️</span>,
            tooltip: 'Ver detalhes',
            onClick: (_, rowData) => handleViewDetails(rowData as HinovaVeiculo),
          },
        ]}
        localization={{
          body: {
            emptyDataSourceMessage: loading
              ? 'Carregando...'
              : 'Nenhum veículo encontrado. Use o campo de busca para pesquisar por placa.',
          },
          pagination: {
            labelRowsSelect: 'veículos',
          },
        }}
        options={{
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          paging: true,
          filtering: false,
          search: false,
        }}
      />

      {selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          open={openVehicleDetails}
          onClose={() => {
            setOpenVehicleDetails(false);
            setSelectedVehicle(null);
          }}
        />
      )}

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}

